import { HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy, RendererFactory2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DomService } from '@nibtravel/shared/util/dom';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import {
  DataFn,
  EventType,
  GtmHelper,
  IGtmProvider,
  IUrlTrackers,
  NavigationFn,
} from './models';

/**
 * Generic implementation that initialise the GTM and provides the most common functionality.
 * By default, it tracks the route changes and dispatch custom events defined within GTM.
 * The default behaviour of the trackers must be defined within the derived class.
 */
@Injectable()
export abstract class BaseGtmProvider implements OnDestroy, IGtmProvider {
  destroy$ = new Subject<void>();

  /** Most brands should use the urlTracker as it tracks the page views with the route path */
  abstract urlTracker: NavigationFn;

  /**
   * TID Specific implementation requires a virtual path. There's a history behind this.
   * The brand don't want to loose historical comparison so we transform the normal route path
   * into a virtual path defined in libs\shared\util\gtm\src\lib\models\gtm.constants.ts
   */
  abstract virtualTracker: NavigationFn;

  /**
   * This is a tracker for campaigns using the UTM query strings
   * In order to ensure consistent results we store the query params into state data
   * and when the route event is triggered, we fetch them.
   * NOTE: In order to ensure lib boundaries, this must be implemented on derived class per app
   *
   * @experimental
   */
  abstract campaignTrackers: DataFn;

  constructor(
    private domService: DomService,
    private router: Router,
    private rendererFactory: RendererFactory2
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  init(gtmCode: string, ga4TagId: string): void {
    if (this.initGtm(gtmCode, ga4TagId)) {
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          map((event) => this.getTrackers(event as NavigationEnd)),
          takeUntil(this.destroy$)
        )
        .subscribe((url: IUrlTrackers) => {
          if (url.route) {
            let page = url.route;
            const utmTrackers = this.campaignTrackers();
            if (utmTrackers) {
              const httpParams = new HttpParams({ fromObject: utmTrackers });
              page = `${url.route}?${httpParams.toString()}`;
            }
            this.domService.addToDataLayer({
              event: EventType.CustomTrackingUrl,
              page,
              ...utmTrackers,
            });
          }

          if (url.virtual) {
            let virtualPage = url.virtual;
            const utmTrackers = this.campaignTrackers();
            if (utmTrackers) {
              const httpParams = new HttpParams({ fromObject: utmTrackers });
              virtualPage = `${url.virtual}?${httpParams.toString()}`;
            }
            this.domService.addToDataLayer({
              event: EventType.VirtualTrackingUrl,
              virtualPage,
              ...this.campaignTrackers(),
            });
          }
        });
    }
  }

  private getTrackers(event: NavigationEnd): IUrlTrackers {
    return {
      route: this.urlTracker(event),
      virtual: this.virtualTracker(event),
    };
  }

  private initGtm(gtmCode: string, ga4TagId: string): boolean {
    try {
      this.domService.addToDataLayer({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
      });
      const scriptSetupStatus = [
        this.setGtmScriptTag(gtmCode),
        this.setGA4ScriptTag(ga4TagId),
      ].every(Boolean);
      return scriptSetupStatus;
    } catch (error) {
      console.log('Failed to initialize GTM: ', error);
      return false;
    }
  }

  private setGtmScriptTag(gtmCode: string): boolean {
    const doc = this.domService.getDocument();
    const renderer = this.rendererFactory.createRenderer(doc, null);
    const script: HTMLScriptElement = renderer.createElement('script');
    script.src = GtmHelper.gtmUrl(gtmCode);
    script.async = true;

    const head: HTMLHeadElement = doc.head;
    renderer.appendChild(head, script);
    return true;
  }

  private setGA4ScriptTag(ga4TagId: string): boolean {
    const doc = this.domService.getDocument();
    const renderer = this.rendererFactory.createRenderer(doc, null);
    const script: HTMLScriptElement = document.createElement('script');
    script.src = GtmHelper.ga4Url(ga4TagId);
    script.async = true;

    const head: HTMLHeadElement = doc.head;
    renderer.appendChild(head, script);
    this.setUpGa4Config(ga4TagId);
    return true;
  }

  private setUpGa4Config(ga4TagId: string): void {
    if (!ga4TagId) throw Error('Please provide a valid google analytics id');
    const doc = this.domService.getDocument();
    const renderer = this.rendererFactory.createRenderer(doc, null);
    const script: HTMLScriptElement = renderer.createElement('script');
    script.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());

    gtag('config', '${ga4TagId}');
    `;
    const head: HTMLHeadElement = doc.head;
    renderer.appendChild(head, script);
  }
}
